import { useMutation, useQueries, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '@luparx/auth';
import { useVehicleColors, useVehicleTypes } from '@luparx/features';
import { ApiError } from '@luparx/api-client';
import type {
  AdministrativeDivision,
  CardPaymentAvailability,
  CheckoutResult,
  ChangeEmailRequest,
  ChangePasswordRequest,
  CreateVehicleRequest,
  ExtendParkingSessionRequest,
  Fine,
  FineDetail,
  CitizenNotification,
  NotificationPreferences,
  PagedResponse,
  UpdateNotificationPreferencesRequest,
  ParkingExtensionOption,
  ParkingPolicy,
  ParkingQuoteResponse,
  ParkingSchedule,
  ParkingSession,
  ParkingSpaceFormat,
  ParkingZone,
  StartParkingSessionRequest,
  TimeCreditsResponse,
  UpdateProfileRequest,
  UpdateVehicleRequest,
  UserProfile,
  Vehicle,
  VehicleAttributeCatalogEntry,
  WalletResponse,
  ZoneGeoJsonFeatureCollection,
} from '@luparx/api-client';

/**
 * TanStack Query hooks over the citizen v0.2 parking domain (CONTRACT.md §API). Every screen
 * talks to these, never to `apiClient.citizen*` directly, so cache keys and invalidation stay in
 * one place — see each mutation's `onSuccess` for exactly what CONTRACT.md's "misma transacción"
 * server-side invariant requires the client to refresh (wallet balance, time credits, sessions).
 */
const KEYS = {
  vehicles: ['citizen', 'vehicles'] as const,
  policy: ['citizen', 'parking', 'policy'] as const,
  activeSessions: ['citizen', 'parking', 'sessions', 'ACTIVE'] as const,
  quote: (zoneId: string, minutes: number) => ['citizen', 'parking', 'quote', zoneId, minutes] as const,
  extensionOptions: (sessionId: string) => ['citizen', 'parking', 'extension-options', sessionId] as const,
  wallet: ['citizen', 'wallet'] as const,
  timeCredits: ['citizen', 'time-credits'] as const,
  schedule: ['citizen', 'parking', 'schedule'] as const,
  zones: ['citizen', 'parking', 'zones'] as const,
  spaceFormat: ['citizen', 'parking', 'space-format'] as const,
  zonesGeoJson: ['citizen', 'parking', 'zones', 'geojson'] as const,
  notifications: (page: number, size: number) => ['citizen', 'notifications', page, size] as const,
  notificationsUnread: ['citizen', 'notifications', 'unread-count'] as const,
  notificationPreferences: ['citizen', 'notifications', 'preferences'] as const,
  cardPayments: ['citizen', 'card-payments'] as const,
  checkout: (id: string) => ['citizen', 'checkouts', id] as const,
};

export function useVehicles(): UseQueryResult<Vehicle[]> {
  const { apiClient } = useAuth();
  return useQuery({ queryKey: KEYS.vehicles, queryFn: () => apiClient.citizenVehicles.list() });
}

export function useCreateVehicle() {
  const { apiClient } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateVehicleRequest) => apiClient.citizenVehicles.create(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.vehicles }),
  });
}

export function useUpdateVehicle() {
  const { apiClient } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateVehicleRequest }) =>
      apiClient.citizenVehicles.update(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.vehicles }),
  });
}

export function useSetPrimaryVehicle() {
  const { apiClient } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.citizenVehicles.setPrimary(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.vehicles }),
  });
}

export function useDeleteVehicle() {
  const { apiClient } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.citizenVehicles.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEYS.vehicles }),
  });
}

export function useParkingPolicy(): UseQueryResult<ParkingPolicy> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: KEYS.policy,
    queryFn: () => apiClient.citizenParking.policy(),
    staleTime: 5 * 60_000,
  });
}

/** Every session across every tenant the citizen belongs to (CONTRACT.md v0.2 rule 3 — the sticky timer is tenant-agnostic), polled so an externally-expiring session eventually drops off. */
export function useActiveParkingSessions(): UseQueryResult<ParkingSession[]> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: KEYS.activeSessions,
    queryFn: () => apiClient.citizenParking.sessions({ status: 'ACTIVE' }),
    refetchInterval: 30_000,
  });
}

/** Live quote for the zone/duration currently selected in the parking flow — server-computed, never estimated client-side (CONTRACT.md v0.2 §Invariantes). */
export function useParkingQuote(
  zoneId: string | null,
  minutes: number | null,
  /**
   * Which car it is for, when the flow already knows (v0.31). Courtesy is limited per plate, so a
   * quote that does not name one cannot say whether the stay is free — and the price on the screen
   * would then not be the price charged. It is part of the cache key for the same reason.
   */
  vehicleId?: string | null,
  plate?: string | null,
): UseQueryResult<ParkingQuoteResponse> {
  const { apiClient } = useAuth();
  const forCar = vehicleId ?? plate ?? '';
  return useQuery({
    queryKey: [...KEYS.quote(zoneId ?? '', minutes ?? 0), forCar],
    queryFn: () =>
      apiClient.citizenParking.quote({
        zoneId: zoneId!,
        minutes: minutes!,
        ...(vehicleId ? { vehicleId } : {}),
        ...(!vehicleId && plate ? { plate } : {}),
      }),
    enabled: Boolean(zoneId) && Boolean(minutes) && minutes! > 0,
  });
}

/**
 * A quote for each duration the municipality sells, so the picker can print the price on every
 * option (v0.6 — the reference product's "30 minutos · ₡300,00").
 *
 * One request per option and no arithmetic here: the tariff ladder is the municipality's and is
 * not linear in most of them, so multiplying the 30-minute price by two to guess the hour would be
 * wrong wherever it matters. The list is short (the municipality's own increments), every answer
 * is cached under the same key the single-quote hook uses, and a failed one simply leaves that
 * option without a price rather than emptying the list.
 */
export function useParkingQuotes(
  zoneId: string | null,
  minutesList: number[],
  vehicleId?: string | null,
  plate?: string | null,
): Map<number, ParkingQuoteResponse> {
  const { apiClient } = useAuth();
  const forCar = vehicleId ?? plate ?? '';
  const results = useQueries({
    queries: minutesList.map((minutes) => ({
      queryKey: [...KEYS.quote(zoneId ?? '', minutes), forCar],
      queryFn: () =>
        apiClient.citizenParking.quote({
          zoneId: zoneId as string,
          minutes,
          ...(vehicleId ? { vehicleId } : {}),
          ...(!vehicleId && plate ? { plate } : {}),
        }),
      enabled: Boolean(zoneId) && minutes > 0,
      staleTime: 60_000,
    })),
  });
  const quotes = new Map<number, ParkingQuoteResponse>();
  results.forEach((result, index) => {
    if (result.data) quotes.set(minutesList[index]!, result.data);
  });
  return quotes;
}

/**
 * Every extension this municipality offers on a running stay, already priced
 * (`GET /citizen/parking/sessions/{id}/extension-options`).
 *
 * Refetched whenever the dialog is opened rather than served from a long cache: `newExpiresAt` and
 * the chargeable minutes are answers about *now*, and a stay that has since crossed the end of the
 * charging day is priced differently from the one this list described a minute ago.
 */
export function useExtensionOptions(
  sessionId: string | null,
  enabled: boolean,
): UseQueryResult<ParkingExtensionOption[]> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: KEYS.extensionOptions(sessionId ?? ''),
    queryFn: () => apiClient.citizenParking.extensionOptions(sessionId as string),
    enabled: Boolean(sessionId) && enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useStartParkingSession() {
  const { apiClient } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: StartParkingSessionRequest) => apiClient.citizenParking.start(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEYS.activeSessions });
      void queryClient.invalidateQueries({ queryKey: KEYS.wallet });
      void queryClient.invalidateQueries({ queryKey: KEYS.timeCredits });
    },
  });
}

export function useExtendParkingSession() {
  const { apiClient } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<ParkingSession, unknown, { id: string; payload: ExtendParkingSessionRequest }>({
    mutationFn: ({ id, payload }) => apiClient.citizenParking.extend(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEYS.activeSessions });
      void queryClient.invalidateQueries({ queryKey: KEYS.wallet });
    },
  });
}

export function useFinishParkingSession() {
  const { apiClient } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<ParkingSession, unknown, string>({
    mutationFn: (id) => apiClient.citizenParking.finish(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEYS.activeSessions });
      void queryClient.invalidateQueries({ queryKey: KEYS.timeCredits });
    },
  });
}

export function useWallet(): UseQueryResult<WalletResponse> {
  const { apiClient } = useAuth();
  return useQuery({ queryKey: KEYS.wallet, queryFn: () => apiClient.citizenWallet.get() });
}

export function useTimeCredits(): UseQueryResult<TimeCreditsResponse> {
  const { apiClient } = useAuth();
  return useQuery({ queryKey: KEYS.timeCredits, queryFn: () => apiClient.citizenTimeCredits.get() });
}

/**
 * The municipality's charging schedule (CONTRACT.md v0.3 §"Horario de cobro"). Refetched on a
 * timer because `chargingNow` is a point-in-time answer: a screen left open across 18:00 has to
 * stop telling the citizen they are about to be charged.
 */
export function useParkingSchedule(): UseQueryResult<ParkingSchedule> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: KEYS.schedule,
    queryFn: () => apiClient.citizenParking.schedule(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/**
 * Zones the citizen can park in, from `GET /citizen/parking/zones`.
 *
 * The 404 branch is kept deliberately: it is what this app does against a server older than the
 * change that published the endpoint, and it degrades to the zones the citizen has already parked
 * in rather than to nothing. No zone is ever invented client-side — a made-up id would only fail
 * later, at the moment of charging.
 */
export function useParkingZones(): UseQueryResult<ParkingZone[]> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: KEYS.zones,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      try {
        return await apiClient.citizenParking.zones();
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 404) throw error;
        const history = await apiClient.citizenParking.sessions({ status: 'ALL' });
        const byId = new Map<string, ParkingZone>();
        for (const session of history) {
          if (!byId.has(session.zoneId)) {
            byId.set(session.zoneId, { id: session.zoneId, code: session.zoneId, name: session.zoneName });
          }
        }
        return [...byId.values()];
      }
    },
  });
}

/**
 * The municipality's bay-code format (CONTRACT.md v0.3 §"Formato del código de espacio").
 *
 * The citizen's field uses it for two things the server would otherwise have to be asked about: the
 * example it shows ("0001" in San José, "E-0001" in Escazú) and the regex it checks a typed code
 * against before spending a round trip on a code that cannot exist. It is configuration, not state:
 * long `staleTime`, and dropped wholesale when the municipality changes (TenantCacheReset).
 */
export function useParkingSpaceFormat(): UseQueryResult<ParkingSpaceFormat> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: KEYS.spaceFormat,
    queryFn: () => apiClient.citizenParking.spaceFormat(),
    staleTime: 10 * 60_000,
  });
}

/** Vehicle types/colours, from the platform catalog — see `@luparx/features`. */
export function useVehicleTypeCatalog(): UseQueryResult<VehicleAttributeCatalogEntry[]> {
  const { apiClient } = useAuth();
  return useVehicleTypes(apiClient);
}

export function useVehicleColorCatalog(): UseQueryResult<VehicleAttributeCatalogEntry[]> {
  const { apiClient } = useAuth();
  return useVehicleColors(apiClient);
}

/**
 * One level of a country's administrative divisions (`GET /catalog/countries/{code}/divisions`).
 *
 * The account screen shows an address as the person would write it — "San José, Escazú, San
 * Rafael" — while the profile stores only the division ids, so the names have to be looked up.
 * The catalog is public and slow-moving, hence the long `staleTime`; the query stays disabled
 * until its parent is known, so the cascade is never asked for children of nothing.
 */
export function useDivisions(
  countryCode: string | undefined,
  level: number,
  parentId: string | undefined,
): UseQueryResult<AdministrativeDivision[]> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: ['catalog', 'divisions', countryCode ?? '', level, parentId ?? 'root'],
    queryFn: () => apiClient.catalog.divisions(countryCode as string, { level, parentId }),
    enabled: Boolean(countryCode) && (level === 1 || Boolean(parentId)),
    staleTime: 10 * 60_000,
  });
}

// ---- Account (CONTRACT.md v0.3 §"Perfil editable") ---------------------------------------------

/**
 * Saves the personal data of CONTRACT.md §2. The server answers with the whole `/me` payload, so
 * the auth context is refreshed from that answer rather than from what the form believed it sent.
 */
export function useUpdateProfile() {
  const { apiClient, refreshProfile } = useAuth();
  return useMutation<UserProfile, unknown, UpdateProfileRequest>({
    mutationFn: (payload) => apiClient.session.updateMe(payload),
    onSuccess: () => {
      void refreshProfile();
    },
  });
}

/** Changing the password signs every other session out (CONTRACT.md v0.3 §1.3) — the screen says so before submitting. */
export function useChangePassword() {
  const { apiClient } = useAuth();
  return useMutation<void, unknown, ChangePasswordRequest>({
    mutationFn: (payload) => apiClient.session.changePassword(payload),
  });
}

/** Starts the e-mail change; the current address keeps working until the new one is verified. */
export function useChangeEmail() {
  const { apiClient } = useAuth();
  return useMutation<void, unknown, ChangeEmailRequest>({
    mutationFn: (payload) => apiClient.session.changeEmail(payload),
  });
}

/**
 * The time zone every parking time is stated in.
 *
 * The municipality's, not the device's (CONTRACT.md v0.3 §"Horario de cobro"): a stay bought in
 * San José expires at a San José clock time, whatever the phone is set to. Falls back to the
 * account's own zone while the schedule is still loading, and to the device only if neither is
 * known — never a hardcoded zone.
 */
export function useTenantTimeZone(): string | undefined {
  const { me } = useAuth();
  const { data: schedule } = useParkingSchedule();
  return schedule?.timeZone ?? me?.user.timeZone ?? undefined;
}

// ---- Fines (CONTRACT.md v0.7) ------------------------------------------------------------------
// Matched by vehicle and never by plate: anybody may register any plate — that is what makes a
// shared family car work — so listing "every citation whose plate matches one I typed into my
// garage" would hand one person another person's fines. The narrowing happens server-side; the
// client only reads what comes back.

export const FINE_KEYS = {
  /** Every fines listing, whatever its page — what a filed defence invalidates. */
  all: ['citizen', 'fines'] as const,
  list: (page: number, size: number) => ['citizen', 'fines', page, size] as const,
  detail: (id: string) => ['citizen', 'fine', id] as const,
};

export function useFines(page: number, size: number): UseQueryResult<PagedResponse<Fine>> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: FINE_KEYS.list(page, size),
    queryFn: () => apiClient.citizenFines.list({ page, size }),
  });
}

/**
 * Pays a fine with the wallet balance (v0.41).
 *
 * <p>Invalidates the wallet as well as the fine, and that is not housekeeping: the balance is on
 * screen in the tab bar while this runs, and a number that still shows the old total after the money
 * left is the app telling the citizen they have more than they do.</p>
 */
export function usePayFine(id: string | undefined) {
  const { apiClient } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.citizenFines.pay(id as string, { method: 'WALLET' }),
    onSuccess: (paid) => {
      // The server answered with the fine in its new state, so it goes straight into the cache: a
      // refetch would show the old status for one frame, and "still unpaid" is the worst frame to
      // flash after somebody just paid.
      queryClient.setQueryData(FINE_KEYS.detail(id ?? ''), paid.fine);
      void queryClient.invalidateQueries({ queryKey: FINE_KEYS.all });
      void queryClient.invalidateQueries({ queryKey: KEYS.wallet });
    },
  });
}

export function useFine(id: string | undefined): UseQueryResult<FineDetail> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: FINE_KEYS.detail(id ?? ''),
    queryFn: () => apiClient.citizenFines.get(id as string),
    enabled: Boolean(id),
  });
}


// ---- Notifications (CONTRACT.md v0.38) --------------------------------------------------------

/**
 * The bell's number.
 *
 * Polled rather than pushed: the alternative is a socket held open by every phone in every
 * municipality to deliver a number that changes a handful of times a day. Sixty seconds is under the
 * time it takes somebody to notice they were not told, and it is one indexed count on the server.
 *
 * Tenant-scoped like everything else here — the key does not name the municipality because
 * `TenantCacheReset` clears the whole citizen cache when it changes, which is what stops one
 * municipality's count being shown under another's name.
 */
export function useUnreadNotificationCount(): UseQueryResult<{ unread: number }> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: KEYS.notificationsUnread,
    queryFn: () => apiClient.citizenNotifications.unreadCount(),
    refetchInterval: 60_000,
  });
}

export function useNotifications(page: number, size: number): UseQueryResult<PagedResponse<CitizenNotification>> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: KEYS.notifications(page, size),
    queryFn: () => apiClient.citizenNotifications.list({ page, size }),
  });
}

export function useMarkNotificationRead() {
  const { apiClient } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.citizenNotifications.markRead(id),
    // Both: the row changed and so did the badge. Refreshing only the list would leave the number on
    // the bell counting something the person is looking at.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['citizen', 'notifications'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const { apiClient } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.citizenNotifications.markAllRead(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['citizen', 'notifications'] });
    },
  });
}

export function useNotificationPreferences(): UseQueryResult<NotificationPreferences> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: KEYS.notificationPreferences,
    queryFn: () => apiClient.citizenNotifications.preferences(),
  });
}

export function useUpdateNotificationPreferences() {
  const { apiClient } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateNotificationPreferencesRequest) =>
      apiClient.citizenNotifications.updatePreferences(payload),
    onSuccess: (updated) => {
      // Written straight into the cache rather than refetched: the server just answered with the row
      // it stored, and a refetch would show the switch flicking back to its old position for one
      // frame while the round trip lands.
      queryClient.setQueryData(KEYS.notificationPreferences, updated);
    },
  });
}

/**
 * Los perímetros dibujados de la municipalidad activa, para el mapa informativo.
 *
 * <p>Sin `bbox`: el mapa del ciudadano muestra las zonas de SU municipalidad, que son decenas, no
 * un recorte de una ciudad entera. El servidor ya topa la respuesta en
 * `luparx.geo.max-zones-per-map` (500 por omisión) y el `bbox` existe para un visor que se mueve,
 * que no es este caso. Cuando lo sea, el parámetro ya está en el cliente.</p>
 *
 * <p>`enabled` lo decide quien llama: esto no se pide hasta que hay zona y bahía válida, para no
 * gastar una petición en una pantalla que el ciudadano quizá no despliegue.</p>
 *
 * <p>Qué llega aquí lo decide el SERVIDOR: en producción sólo geometrías verificadas. El cliente no
 * filtra por estado —un filtro de frontend no es una garantía— y se limita a rotular lo que
 * recibe.</p>
 */
export function useZonesGeoJson(habilitada: boolean): UseQueryResult<ZoneGeoJsonFeatureCollection> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: KEYS.zonesGeoJson,
    queryFn: () => apiClient.citizenParking.zonesGeoJson(),
    enabled: habilitada,
    // Un perímetro municipal no cambia mientras alguien estaciona.
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}


// ---- Pago con tarjeta ------------------------------------------------------------------------
//
// Acá no se toca una tarjeta. `iniciar` devuelve una dirección del proveedor a la que la pantalla
// NAVEGA; el número se teclea allá. De vuelta, lo único que confirma el pago es `credited`, que lo
// dice el servidor: nunca se deduce del estado ni de haber vuelto de la página.

export function useCardPaymentAvailability(): UseQueryResult<CardPaymentAvailability> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: KEYS.cardPayments,
    queryFn: () => apiClient.citizenCheckouts.availability(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Abre el traspaso.
 *
 * <p>La clave de idempotencia la acuña quien llama y se guarda mientras dure el intento, no se
 * genera por petición: es lo que hace que un doble clic, o un reintento tras una desconexión,
 * devuelvan el MISMO traspaso en vez de abrir un segundo cobro.</p>
 */
export function useStartCheckout() {
  const { apiClient } = useAuth();
  return useMutation({
    mutationFn: ({ amountMinor, idempotencyKey }: { amountMinor: number; idempotencyKey: string }) =>
      apiClient.citizenCheckouts.start(amountMinor, idempotencyKey),
  });
}

/** La vuelta de la página del proveedor. Quema el token y deja el resultado asentado. */
export function useCheckoutReturn() {
  const { apiClient } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => apiClient.citizenCheckouts.returned(token),
    onSuccess: (resultado) => {
      // El saldo sólo pudo cambiar si el servidor dice que acreditó.
      if (resultado.credited) {
        void queryClient.invalidateQueries({ queryKey: KEYS.wallet });
      }
    },
  });
}

/** Dónde va un traspaso. De sólo lectura y seguro de consultar. */
export function useCheckoutStatus(checkoutId: string | null): UseQueryResult<CheckoutResult> {
  const { apiClient } = useAuth();
  return useQuery({
    queryKey: KEYS.checkout(checkoutId ?? ''),
    queryFn: () => apiClient.citizenCheckouts.status(checkoutId as string),
    enabled: Boolean(checkoutId),
  });
}
